import React, { useState, useContext, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import { FaPlus } from 'react-icons/all';

/**
 * Shared UI components from SyncUp design system
 * These provide consistent styling, accessibility, and modal behavior
 * across the application.
 */
import {
  TmoButtons,
  TmoInput,
  TmoButtons as TmoButton,
  FormModal,
  TmoSvg,
  TmoDropDownSelect,
  withModal
} from '@tmobile/tmo-bps-syncup-web-component-lib';

/**
 * Services, helpers, and application context
 */
import featureFlagService from '../../services/featureFlagsService';
import Fetcher from '../Fetcher';
import FeatureFlagItem from './FeatureFlagItem';
import SyncUpProductContext from '../../context/SyncUpProductContext';
import StatusContext from '../../context/StatusContext';
import useVisible from '../../hooks/useVisible';
import { trackCustomEvents } from '../../utils/appInsight_analytics';
import { getRandomString } from '../../utils/stringUtils';
import { hasValue } from '../../utils/helper_functions';

/**
 * UI helpers and shared components
 */
import Error from '../../components/Error/Error';
import PageHeader from '../../components/uiHelpers/pageHeader/PageHeader';

import style from './FeatureFlags.module.css';

/**
 * FeatureFlags
 * -----------------
 * Primary container for managing feature flags of a SyncUp application.
 *
 * Responsibilities:
 * - Fetch and display feature flags
 * - Add new feature flags via modal
 * - Delete existing feature flags
 * - Refresh data on demand
 *
 * This component acts as the orchestration layer and delegates
 * rendering and actions to child components where appropriate.
 */
function FeatureFlags({ match: { url }, history, openModal }) {
  /**
   * Provides current SyncUp application metadata
   * (e.g., app name used in API calls)
   */
  const syncUpProductContext = useContext(SyncUpProductContext);

  /**
   * Local state holding the list of feature flags
   * populated via Fetcher
   */
  const [featureFlags, setFeatureFlags] = useState([]);

  /**
   * Global status handlers used for success/error messaging
   * (toast notifications, banners, etc.)
   */
  const { addSuccessMessage, addErrorMessage } = useContext(StatusContext);

  /**
   * Controls visibility of the global loading spinner
   */
  const { visible, setVisible } = useVisible(false);

  /**
   * Key used to force re-mounting the Fetcher component.
   * Changing this value triggers a fresh API call.
   */
  const [refreshKey, setRefreshKey] = useState(
    getRandomString() + syncUpProductContext.syncUpAppName
  );

  /**
   * Forces a re-fetch of feature flags by updating Fetcher key.
   * Memoized to avoid unnecessary re-creations.
   */
  const forceRefresh = useCallback(() => {
    setRefreshKey(getRandomString() + syncUpProductContext.syncUpAppName);
  }, [syncUpProductContext.syncUpAppName]);

  /**
   * Opens the "Add Feature Flag" modal.
   *
   * Flow:
   * 1. Open modal and wait for user confirmation
   * 2. Track analytics event
   * 3. Call create API
   * 4. Refresh data and show success/error feedback
   */
  const openAddModal = async () => {
    const { isConfirmed, formValues } = await openModal();

    if (isConfirmed) {
      trackCustomEvents(`Add New Feature Flag`);
      setVisible(true);

      /**
       * Construct API request body from modal form values
       */
      const body = {
        featureId: formValues.featureId,
        isEnabled: Boolean(
          formValues.isEnabled === 'true' || formValues.isEnabled === true
        )
      };

      try {
        await featureFlagService.createFeatureFlag({
          syncUpAppName: syncUpProductContext.syncUpAppName,
          body
        });

        forceRefresh();
        addSuccessMessage({
          message: `Successfully added feature flag: ${formValues.featureId}`
        });
      } catch (error) {
        addErrorMessage({ error });
      } finally {
        setVisible(false);
      }
    }
  };

  /**
   * Handles deletion of a feature flag.
   * Invoked from FeatureFlagItem after confirmation.
   */
  const handleDeleteFeatureFlagClicked = async flagId => {
    try {
      await featureFlagService.deleteFeatureFlag({
        flagId,
        syncUpAppName: syncUpProductContext.syncUpAppName
      });

      forceRefresh();
      addSuccessMessage({
        message: `Feature flag deleted successfully`
      });
    } catch (error) {
      addErrorMessage({ error });
    }
  };

  /**
   * Form field configuration used by FormModal
   * to dynamically render inputs for adding a feature flag.
   */
  const formFieldSpecs = [
    {
      fieldName: 'featureId',
      labelText: 'Feature ID',
      formComponent: TmoInput,
      inputType: 'primary',
      required: true,
      placeholder: 'Enter feature flag ID (e.g., enable-new-ui)'
    },
    {
      fieldName: 'description',
      labelText: 'Description',
      formComponent: TmoInput,
      inputType: 'primary',
      required: true,
      placeholder: 'Describe what this feature flag controls'
    },
    {
      fieldName: 'isEnabled',
      labelText: 'Initial Status',
      formComponent: TmoDropDownSelect,
      required: true,
      optionsList: [
        { key: 'false', value: 'Disabled' },
        { key: 'true', value: 'Enabled' }
      ],
      defaultSelectedValue: { key: 'false', value: 'Disabled' },
      defaultValue: 'false'
    }
  ];

  /**
   * Fetcher action callback.
   * Retrieves all feature flags for the current SyncUp application.
   */
  const actionCallback = useCallback(async () => {
    try {
      return await featureFlagService.getFeatureFlags({
        syncUpAppName: syncUpProductContext.syncUpAppName
      });
    } catch (error) {
      console.error('Error loading feature flags:', error);
      throw error;
    }
  }, [syncUpProductContext.syncUpAppName]);

  /**
   * Handles data returned from Fetcher and updates local state.
   */
  const handleLoadData = useCallback(data => {
    if (data) {
      setFeatureFlags(data);
    }
  }, []);

  return (
    <>
      {/* Page header with primary actions */}
      <PageHeader
        primary
        title="Feature Flags"
        subtitle={`Manage feature flags for the ${syncUpProductContext.syncUpAppName} application.`}
      >
        {/* Add new feature flag */}
        <TmoButton
          type="magenta_primary"
          icon={<FaPlus />}
          onClick={openAddModal}
        >
          Add New Feature Flag
        </TmoButton>

        {/* Manual refresh button */}
        <TmoButtons onClick={forceRefresh} type="magenta_primary">
          Refresh
        </TmoButtons>

        {/* Modal definition controlled by withModal HOC */}
        <FormModal
          title="Add new Feature Flag"
          fieldSpecs={formFieldSpecs}
          modalType="slide"
        />
      </PageHeader>

      {/* Global loading indicator */}
      {visible && <TmoSvg className={style.spinner} svgId="loading-spinner" />}

      <div>
        <Fetcher
          key={refreshKey}
          action={actionCallback}
          onLoad={handleLoadData}
          render={() =>
            hasValue(featureFlags) ? (
              <div className={style.items}>
                {featureFlags.map(flag => (
                  <FeatureFlagItem
                    key={flag.id || flag.featureId}
                    data={flag}
                    onDelete={handleDeleteFeatureFlagClicked}
                    forceRefresh={forceRefresh}
                  />
                ))}
              </div>
            ) : (
              <Error
                heading="No Feature Flags are available"
                message="Please add a new feature flag"
              />
            )
          }
        />
      </div>
    </>
  );
}

/**
 * Export wrapped with:
 * - withRouter: injects routing props
 * - withModal: provides modal lifecycle and openModal handler
 */
export default withModal(withRouter(FeatureFlags));
